import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Award, CalendarDays, Camera, Clapperboard, Film, Loader2, MapPin, Newspaper,
  PartyPopper, Play, Search, Sparkles, Users, X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta } from "../components/service/ServiceBits";
import api, { API_BASE } from "../lib/api";

const CATEGORIES = [
  { key: "All", icon: Sparkles }, { key: "How To Pack", icon: Clapperboard },
  { key: "Influencers", icon: Film }, { key: "Customer Stories", icon: Users },
  { key: "PR & Media", icon: Newspaper }, { key: "Events", icon: PartyPopper },
  { key: "Team IndiaFast", icon: Users }, { key: "Achievements", icon: Award },
  { key: "Behind the Scenes", icon: Camera }, { key: "Announcements", icon: Newspaper },
  { key: "Photos", icon: Camera },
];
const TYPE_FILTERS = [
  { key: "", label: "All Types" }, { key: "video", label: "Videos" }, { key: "reel", label: "Reels" },
  { key: "image", label: "Photos" }, { key: "article", label: "Articles" },
  { key: "press_release", label: "Press" }, { key: "event", label: "Events" }, { key: "achievement", label: "Achievements" },
];
const VIDEO_TYPES = new Set(["video", "reel"]);
const REEL_CATS = new Set(["Influencers"]);
const MASONRY_CATS = new Set(["Photos", "Events", "Behind the Scenes"]);
const EDITORIAL_CATS = new Set(["PR & Media"]);
const TIMELINE_CATS = new Set(["Achievements"]);
const STORY_CATS = new Set(["Customer Stories", "Customer Feedback"]);

export const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};
export const vimeoId = (url) => {
  const m = String(url || "").match(/vimeo\.com\/(\d{6,})/);
  return m ? m[1] : null;
};
const fileUrl = (item, size = "card") => (item.file_path ? `${API_BASE}/api/mediahub/public/file/${item.id}/${size}` : null);
const extThumb = (item) => {
  const y = ytId(item.external_url);
  if (y) return `https://i.ytimg.com/vi/${y}/hqdefault.jpg`;
  return null;
};
export const thumbOf = (item) => fileUrl(item, "card") || fileUrl({ ...item, file_path: item.thumb_path }, "orig") || extThumb(item);
const focalStyle = (item) => ({ objectPosition: `${item.focal_x ?? 50}% ${item.focal_y ?? 50}%` });
const isVideoItem = (m) => VIDEO_TYPES.has(m.media_type) || ytId(m.external_url) || vimeoId(m.external_url);

function PlayBadge({ big }) {
  return (
    <span className={`grid place-items-center rounded-full bg-saffron text-white shadow-xl shadow-saffron/40 transition-transform group-hover:scale-110 ${big ? "h-16 w-16" : "h-11 w-11"}`}>
      <Play className={`${big ? "h-7 w-7" : "h-4 w-4"} ml-0.5`} fill="currentColor" />
    </span>
  );
}

function MetaLine({ m, light }) {
  return (
    <p className={`mt-1 flex flex-wrap items-center gap-x-2 text-[11px] ${light ? "text-slate-300" : "text-slate-500"}`}>
      {m.date && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{m.date}</span>}
      {m.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{m.location}</span>}
      {m.creator && <span>· {m.creator}</span>}
      {m.duration && <span>· {m.duration}</span>}
    </p>
  );
}

function CatBadge({ c, dark }) {
  return (
    <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider backdrop-blur ${dark ? "bg-navy/80 text-saffron" : "bg-saffron/10 text-saffron"}`}>{c}</span>
  );
}

export default function MediaHub() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [hero, setHero] = useState(null);
  const [stats, setStats] = useState(null);
  const [contact, setContact] = useState(null);
  const [cat, setCat] = useState("All");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [player, setPlayer] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const LIMIT = 24;

  usePageMeta(
    "IndiaFast Media & Videos | Stories, Events, Customer Experiences & More",
    "Explore IndiaFast Global Couriers through videos, customer stories, packing guides, influencer collaborations, events, company achievements, PR and behind-the-scenes moments.",
    "/media-videos"
  );

  const load = useCallback(async (resetSkip = 0, append = false) => {
    append ? setMoreLoading(true) : setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, skip: resetSkip });
      if (cat !== "All") params.set("category", cat);
      if (type) params.set("type", type);
      if (q) params.set("q", q);
      const r = await api.get(`/mediahub/public?${params}`);
      setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      setTotal(r.data.total);
      setSkip(resetSkip + r.data.items.length);
    } catch {} finally { setLoading(false); setMoreLoading(false); }
  }, [cat, type, q]);

  useEffect(() => { load(0, false); }, [load]);
  useEffect(() => {
    api.get("/mediahub/public/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/mediahub/public/hero").then((r) => setHero(r.data)).catch(() => {});
    api.get("/public/home").then((r) => { setStats(r.data.stats); setContact(r.data.contact); }).catch(() => {});
  }, []);

  const openItem = (m) => { isVideoItem(m) ? setPlayer(m) : setLightbox(m); };

  const grouped = useMemo(() => {
    if (cat !== "All") return { [cat]: items };
    const map = {};
    for (const m of items) (map[m.category] = map[m.category] || []).push(m);
    return map;
  }, [items, cat]);

  const heroThumb = hero ? (fileUrl(hero, "orig") || extThumb(hero)) : null;
  const statTiles = stats ? [
    [stats.countries_served || "200+", "Countries Served"], [stats.pincodes || "30,000+", "PIN Codes Covered"],
    [`${stats.courier_partners || 17}+`, "Courier Partners"], [stats.experience || "5+", "Years of Experience"],
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="media-hub-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-20 pt-32 sm:pt-36" data-testid="media-hero">
        {heroThumb && <img src={heroThumb} alt={hero?.title || "IndiaFast media"} className="absolute inset-0 h-full w-full object-cover opacity-30" style={hero ? focalStyle(hero) : undefined} />}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/80 to-navy" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">Media &amp; Videos</p>
          <h1 className="animate-fade-up stagger-1 mx-auto mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            IndiaFast <span className="text-saffron">in Action</span>
          </h1>
          <p className="animate-fade-up stagger-2 mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Stories. People. Deliveries. Milestones. See what happens behind every IndiaFast shipment.
          </p>
          {hero && isVideoItem(hero) && (
            <button onClick={() => setPlayer(hero)} data-testid="media-hero-play" aria-label="Play featured video"
                    className="group mx-auto mt-8 block">
              <PlayBadge big />
            </button>
          )}
          <div className="animate-fade-up stagger-3 mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="#media-featured" data-testid="media-hero-watch"
               className="rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
              Watch Our Stories
            </a>
            <a href="#media-grid" data-testid="media-hero-explore"
               className="rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
              Explore Videos
            </a>
          </div>
        </div>
      </section>

      {/* STICKY CATEGORY NAV */}
      <div className="sticky top-16 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl" data-testid="media-catnav">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6">
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => { setCat(c.key); setSkip(0); }} data-testid={`media-cat-${c.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-all ${cat === c.key ? "bg-navy text-saffron shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-saffron/10 hover:text-saffron"}`}>
              <c.icon className="h-3.5 w-3.5" /> {c.key}
            </button>
          ))}
        </div>
      </div>

      {/* SEARCH + TYPE FILTER */}
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 pt-8 sm:px-6" data-testid="media-filters">
        <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); setSkip(0); }}
              className="flex min-w-60 flex-1 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="media-search"
                 placeholder="Search IndiaFast stories…" className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400" />
        </form>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map((t) => (
            <button key={t.key} onClick={() => { setType(t.key); setSkip(0); }} data-testid={`media-type-${t.key || "all"}`}
                    className={`rounded-full px-3.5 py-2 text-[11px] font-bold transition-colors ${type === t.key ? "bg-saffron text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-saffron/40 hover:text-saffron"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* FEATURED */}
      {cat === "All" && !type && !q && featured.length > 0 && (
        <section id="media-featured" className="mx-auto max-w-7xl scroll-mt-32 px-4 pt-12 sm:px-6" data-testid="media-featured">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Featured From IndiaFast</p>
          <div className="mt-5 grid gap-4 lg:grid-cols-5">
            <button onClick={() => openItem(featured[0])} data-testid="media-featured-main"
                    className="group relative overflow-hidden rounded-3xl text-left shadow-xl lg:col-span-3">
              <img src={thumbOf(featured[0])} alt={featured[0].title} className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focalStyle(featured[0])} />
              <span className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
              <span className="absolute inset-0 grid place-items-center">{isVideoItem(featured[0]) && <PlayBadge big />}</span>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <CatBadge c={featured[0].category} dark />
                <h3 className="mt-2 font-heading text-xl font-extrabold text-white sm:text-2xl">{featured[0].title}</h3>
                {featured[0].description && <p className="mt-1 line-clamp-2 text-xs text-slate-300">{featured[0].description}</p>}
                <MetaLine m={featured[0]} light />
              </div>
            </button>
            <div className="grid gap-4 sm:grid-cols-3 lg:col-span-2 lg:grid-cols-1">
              {featured.slice(1, 4).map((m) => (
                <button key={m.id} onClick={() => openItem(m)} data-testid={`media-featured-side-${m.id}`}
                        className="group relative overflow-hidden rounded-2xl text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
                  <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-32 w-full object-cover transition-transform duration-500 group-hover:scale-105 lg:h-full lg:min-h-24" style={focalStyle(m)} />
                  <span className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent" />
                  {isVideoItem(m) && <span className="absolute right-3 top-3"><PlayBadge /></span>}
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <p className="line-clamp-1 font-heading text-sm font-bold text-white">{m.title}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-saffron">{m.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTENT */}
      <main id="media-grid" className="mx-auto max-w-7xl scroll-mt-32 px-4 py-12 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-slate-400" data-testid="media-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading media…</div>
        ) : !items.length ? (
          <div className="rounded-3xl border border-slate-200 bg-white py-24 text-center shadow-sm" data-testid="media-empty">
            <Sparkles className="mx-auto h-10 w-10 text-saffron/50" />
            <p className="mt-4 font-heading text-xl font-bold text-navy">More Stories Coming Soon</p>
            <p className="mt-1 text-sm text-slate-500">We're capturing new moments across the IndiaFast network.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([category, list]) => (
            <CategorySection key={category} category={category} list={list} onOpen={openItem} />
          ))
        )}
        {!loading && items.length < total && (
          <div className="mt-10 text-center">
            <button onClick={() => load(skip, true)} disabled={moreLoading} data-testid="media-load-more"
                    className="rounded-full border border-slate-300 bg-white px-8 py-3 text-sm font-bold text-navy shadow-sm transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
              {moreLoading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : `Load More (${total - items.length} remaining)`}
            </button>
          </div>
        )}
      </main>

      {/* STATS */}
      {statTiles.length > 0 && (
        <section className="border-y border-slate-200 bg-white py-12" data-testid="media-stats">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 sm:px-6 lg:grid-cols-4">
            {statTiles.map(([v, l], i) => (
              <div key={i} className="text-center" data-testid={`media-stat-${i}`}>
                <p className="font-heading text-3xl font-extrabold text-navy sm:text-4xl">{v}</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-saffron">{l}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FINAL BRAND STORY */}
      <section className="relative overflow-hidden bg-navy py-20 sm:py-24" data-testid="media-story">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-emerald-600/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-saffron/15 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">Every Shipment Has a Story.</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">And this is where we share ours.</p>
          <a href="#media-grid" data-testid="media-story-cta"
             className="mt-8 inline-flex items-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
            Explore More IndiaFast Stories <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-slate-50 py-16 sm:py-20" data-testid="media-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl lg:text-4xl">Stay Connected With IndiaFast</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500 sm:text-base">
            Follow our journey, discover useful shipping tips and see what's happening across the IndiaFast network.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/rate-calculator" data-testid="media-cta-quote" className="rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Get a Quote</Link>
            <Link to="/support" data-testid="media-cta-contact" className="rounded-full border border-navy/20 px-8 py-3.5 text-sm font-bold text-navy hover:border-saffron hover:text-saffron">Contact Us</Link>
          </div>
        </div>
      </section>

      <Footer contact={contact} />
      {player && <PlayerModal m={player} onClose={() => setPlayer(null)} />}
      {lightbox && <Lightbox m={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function CategorySection({ category, list, onOpen }) {
  const titles = {
    "How To Pack": ["Learn How to Pack Like a Professional", "Simple packing guides to protect your shipment in transit."],
    "Influencers": ["IndiaFast With Influencers", "Creators sharing their IndiaFast experience."],
    "Customer Stories": ["Our Customers Tell the Story", "Real experiences from the IndiaFast network."],
    "Customer Feedback": ["What Customers Say", "Feedback from shippers across India."],
    "PR & Media": ["IndiaFast in the News", "Coverage, interviews and press releases."],
    "Events": ["Events & Moments", "Launches, celebrations and milestones."],
    "Team IndiaFast": ["The People Behind Every Delivery", "Meet the teams that move your world."],
    "Achievements": ["Milestones We're Proud Of", "The IndiaFast journey so far."],
    "Behind the Scenes": ["Behind the Scenes at IndiaFast", "Sorting, packing, pickups — the real action."],
    "Announcements": ["Announcements", "Latest updates from IndiaFast."],
    "Photos": ["IndiaFast Through the Lens", "A visual journey across our network."],
  };
  const [title, sub] = titles[category] || [category, ""];
  return (
    <section className="mb-14 scroll-mt-32" data-testid={`media-section-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">{category}</p>
        <h2 className="mt-2 font-heading text-xl font-extrabold tracking-tight text-navy sm:text-2xl lg:text-3xl">{title}</h2>
        {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
      </div>
      {TIMELINE_CATS.has(category) ? <Timeline list={list} onOpen={onOpen} />
        : EDITORIAL_CATS.has(category) ? <Editorial list={list} onOpen={onOpen} />
        : MASONRY_CATS.has(category) ? <Masonry list={list} onOpen={onOpen} />
        : REEL_CATS.has(category) ? <ReelGrid list={list} onOpen={onOpen} />
        : STORY_CATS.has(category) ? <StoryGrid list={list} onOpen={onOpen} />
        : <VideoGrid list={list} onOpen={onOpen} />}
    </section>
  );
}

function VideoGrid({ list, onOpen }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {list.map((m) => (
        <button key={m.id} onClick={() => onOpen(m)} data-testid={`media-card-${m.id}`}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
          <div className="relative aspect-video overflow-hidden bg-navy">
            {thumbOf(m) && <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focalStyle(m)} />}
            <span className="absolute inset-0 grid place-items-center">{isVideoItem(m) && <PlayBadge />}</span>
            <span className="absolute left-3 top-3"><CatBadge c={m.category} dark /></span>
          </div>
          <div className="p-4">
            <h3 className="line-clamp-1 font-heading text-sm font-bold text-navy">{m.title}</h3>
            {m.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{m.description}</p>}
            <MetaLine m={m} />
          </div>
        </button>
      ))}
    </div>
  );
}

function ReelGrid({ list, onOpen }) {
  return (
    <div className="flex snap-x gap-4 overflow-x-auto pb-3 sm:grid sm:grid-cols-4 sm:overflow-visible lg:grid-cols-5">
      {list.map((m) => (
        <button key={m.id} onClick={() => onOpen(m)} data-testid={`media-reel-${m.id}`}
                className="group relative w-40 shrink-0 snap-start overflow-hidden rounded-2xl text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl sm:w-auto">
          <div className="relative aspect-[9/16] overflow-hidden bg-navy">
            {thumbOf(m) && <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focalStyle(m)} />}
            <span className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-transparent" />
            <span className="absolute inset-0 grid place-items-center">{isVideoItem(m) && <PlayBadge />}</span>
            <div className="absolute inset-x-0 bottom-0 p-3">
              {m.creator && <p className="text-[10px] font-bold text-saffron">@{m.creator}</p>}
              <p className="line-clamp-2 text-xs font-bold text-white">{m.title}</p>
              {m.platform && <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-300">{m.platform}</p>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function StoryGrid({ list, onOpen }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {list.map((m) => (
        <button key={m.id} onClick={() => onOpen(m)} data-testid={`media-story-card-${m.id}`}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
          <div className="relative aspect-[4/5] overflow-hidden bg-navy">
            {thumbOf(m) && <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focalStyle(m)} />}
            <span className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-transparent" />
            <span className="absolute inset-0 grid place-items-center">{isVideoItem(m) && <PlayBadge />}</span>
            <div className="absolute inset-x-0 bottom-0 p-4">
              <p className="line-clamp-2 text-sm font-bold text-white">{m.title}</p>
              <MetaLine m={m} light />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Editorial({ list, onOpen }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {list.map((m) => (
        <button key={m.id} onClick={() => m.external_url && !isVideoItem(m) ? window.open(m.external_url, "_blank") : onOpen(m)} data-testid={`media-article-${m.id}`}
                className="group flex overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
          <div className="relative w-36 shrink-0 overflow-hidden bg-navy sm:w-44">
            {thumbOf(m) && <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focalStyle(m)} />}
          </div>
          <div className="min-w-0 p-4">
            <CatBadge c={m.category} />
            <h3 className="mt-2 line-clamp-2 font-heading text-sm font-bold text-navy">{m.title}</h3>
            {m.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{m.description}</p>}
            <MetaLine m={m} />
            {m.external_url && !isVideoItem(m) && <p className="mt-2 flex items-center gap-1 text-[11px] font-bold text-saffron">Read More <ArrowRight className="h-3 w-3" /></p>}
          </div>
        </button>
      ))}
    </div>
  );
}

function Masonry({ list, onOpen }) {
  return (
    <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4">
      {list.map((m) => (
        <button key={m.id} onClick={() => onOpen(m)} data-testid={`media-photo-${m.id}`}
                className="group relative block w-full break-inside-avoid overflow-hidden rounded-2xl text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl">
          {thumbOf(m) ? (
            <img src={thumbOf(m)} alt={m.title} loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focalStyle(m)} />
          ) : (
            <div className="grid aspect-square w-full place-items-center bg-navy"><Camera className="h-8 w-8 text-white/30" /></div>
          )}
          {isVideoItem(m) && <span className="absolute inset-0 grid place-items-center"><PlayBadge /></span>}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/85 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
            <p className="line-clamp-1 text-xs font-bold text-white">{m.title}</p>
            <MetaLine m={m} light />
          </div>
        </button>
      ))}
    </div>
  );
}

function Timeline({ list, onOpen }) {
  return (
    <div className="relative ml-4 space-y-6 border-l-2 border-saffron/30 pl-6 sm:ml-8">
      {list.map((m, i) => (
        <button key={m.id} onClick={() => onOpen(m)} data-testid={`media-milestone-${m.id}`}
                className="group relative block w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-saffron/40 hover:shadow-xl sm:max-w-2xl">
          <span className="absolute -left-[34px] top-5 grid h-5 w-5 place-items-center rounded-full bg-saffron ring-4 ring-saffron/20 sm:-left-[42px]">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
          </span>
          <div className="flex items-start gap-4">
            {thumbOf(m) && <img src={thumbOf(m)} alt={m.title} loading="lazy" className="h-16 w-24 shrink-0 rounded-lg object-cover" style={focalStyle(m)} />}
            <div className="min-w-0">
              {m.date && <p className="text-[10px] font-bold uppercase tracking-wider text-saffron">{m.date}</p>}
              <h3 className="mt-0.5 font-heading text-base font-bold text-navy">{m.title}</h3>
              {m.description && <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{m.description}</p>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function PlayerModal({ m, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);
  const y = ytId(m.external_url);
  const v = vimeoId(m.external_url);
  const vertical = m.media_type === "reel" || (m.width && m.height && m.height > m.width);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-navy/90 p-4 backdrop-blur-sm" onClick={onClose} data-testid="media-player-modal">
      <div ref={ref} className={`w-full overflow-hidden rounded-2xl bg-black shadow-2xl ${vertical ? "max-w-sm" : "max-w-4xl"}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-navy px-4 py-3">
          <p className="line-clamp-1 font-heading text-sm font-bold text-white">{m.title}</p>
          <button onClick={onClose} data-testid="media-player-close" aria-label="Close" className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-saffron"><X className="h-4 w-4" /></button>
        </div>
        <div className={vertical ? "aspect-[9/16]" : "aspect-video"}>
          {y ? (
            <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${y}?rel=0`} title={m.title} allow="accelerometer; encrypted-media; picture-in-picture" allowFullScreen data-testid="media-player-iframe" />
          ) : v ? (
            <iframe className="h-full w-full" src={`https://player.vimeo.com/video/${v}`} title={m.title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="media-player-iframe" />
          ) : m.file_path ? (
            <video className="h-full w-full" controls playsInline preload="metadata" poster={fileUrl(m, "card") || undefined} data-testid="media-player-video"
                   src={`${API_BASE}/api/mediahub/public/file/${m.id}/orig`} />
          ) : (
            <div className="grid h-full place-items-center text-sm text-slate-400">Video unavailable</div>
          )}
        </div>
        {(m.description || m.date) && (
          <div className="bg-navy px-4 py-3">
            {m.description && <p className="text-xs leading-relaxed text-slate-300">{m.description}</p>}
            <MetaLine m={m} light />
          </div>
        )}
      </div>
    </div>
  );
}

function Lightbox({ m, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-navy/90 p-4 backdrop-blur-sm" onClick={onClose} data-testid="media-lightbox">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-navy shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3">
          <p className="line-clamp-1 font-heading text-sm font-bold text-white">{m.title}</p>
          <button onClick={onClose} data-testid="media-lightbox-close" aria-label="Close" className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-saffron"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid max-h-[70vh] place-items-center bg-black/40">
          <img src={fileUrl(m, "orig")} alt={m.title} className="max-h-[70vh] w-auto max-w-full object-contain" />
        </div>
        {(m.description || m.date) && (
          <div className="px-4 py-3">
            {m.description && <p className="text-xs text-slate-300">{m.description}</p>}
            <MetaLine m={m} light />
          </div>
        )}
      </div>
    </div>
  );
}
